import { baseApi } from "./api";

// ────────────────────────────────────────────────────────────────────────────
// RTK Query endpoints (used by V2 slice, invoiceV2Slice, autosaveMiddlewareV2)
// Per migration spec: no invalidatesTags - the V2 slice is the live edit
// buffer and writes mutation responses back via slice actions, not via RTK
// refetch. providesTags is set on the two queries to enable updateQueryData.
// ────────────────────────────────────────────────────────────────────────────
export const aufmassApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getProjectAufmass: build.query({
			query: (projectId) => ({
				url: `projects/${projectId}/aufmass`,
			}),
			providesTags: (_r, _e, projectId) => [
				{ type: "Aufmass", id: `LIST-${projectId}` },
			],
		}),
		getAufmassDetailsV2: build.query({
			query: ({ projectId, invoiceId }) => ({
				url: `projects/${projectId}/aufmass/invoice/${invoiceId}/v2`,
			}),
			providesTags: (_r, _e, { invoiceId }) => [
				{ type: "Aufmass", id: `invoice-${invoiceId}` },
			],
		}),
		getAufmassById: build.query({
			query: ({ projectId, aufmassId }) => ({
				url: `projects/${projectId}/aufmass/${aufmassId}`,
			}),
			providesTags: (_r, _e, { aufmassId }) => [
				{ type: "Aufmass", id: aufmassId },
			],
		}),
		saveAufmassDetails: build.mutation({
			query: ({ projectId, invoiceId, body }) => ({
				url: `projects/${projectId}/aufmass/invoice/${invoiceId}`,
				method: "POST",
				body,
			}),
		}),
		createAufmass: build.mutation({
			query: ({ projectId, body }) => ({
				url: `projects/${projectId}/aufmass`,
				method: "POST",
				body,
			}),
		}),
		updateAufmass: build.mutation({
			query: ({ projectId, aufmassId, body }) => ({
				url: `projects/${projectId}/aufmass/${aufmassId}`,
				method: "PUT",
				body,
			}),
		}),
		deleteAufmass: build.mutation({
			query: ({ projectId, aufmassId }) => ({
				url: `projects/${projectId}/aufmass/${aufmassId}`,
				method: "DELETE",
			}),
		}),
		addAufmassGroup: build.mutation({
			query: ({ projectId, aufmassId, body }) => ({
				url: `projects/${projectId}/aufmass/${aufmassId}/groups`,
				method: "POST",
				body,
			}),
		}),
		addAufmassGroupV2: build.mutation({
			query: ({ projectId, aufmassId, body }) => ({
				url: `projects/${projectId}/aufmass/${aufmassId}/groups/v2`,
				method: "POST",
				body,
			}),
		}),
		updateAufmassGroup: build.mutation({
			query: ({ projectId, aufmassId, groupId, body }) => ({
				url: `projects/${projectId}/aufmass/${aufmassId}/groups/${groupId}`,
				method: "PUT",
				body,
			}),
		}),
		deleteAufmassGroup: build.mutation({
			query: ({ projectId, aufmassId, groupId }) => ({
				url: `projects/${projectId}/aufmass/${aufmassId}/groups/${groupId}`,
				method: "DELETE",
			}),
		}),
		addAufmassLine: build.mutation({
			query: ({ projectId, aufmassId, groupId, body }) => ({
				url: `projects/${projectId}/aufmass/${aufmassId}/groups/${groupId}/lines`,
				method: "POST",
				body,
			}),
		}),
		updateAufmassLine: build.mutation({
			query: ({ projectId, aufmassId, groupId, lineId, body }) => ({
				url: `projects/${projectId}/aufmass/${aufmassId}/groups/${groupId}/lines/${lineId}`,
				method: "PUT",
				body,
			}),
		}),
		deleteAufmassLine: build.mutation({
			query: ({ projectId, aufmassId, groupId, lineId }) => ({
				url: `projects/${projectId}/aufmass/${aufmassId}/groups/${groupId}/lines/${lineId}`,
				method: "DELETE",
			}),
		}),
		revalidateAufmass: build.mutation({
			query: ({ projectId, aufmassId }) => ({
				url: `projects/${projectId}/aufmass/${aufmassId}/revalidate`,
				method: "PATCH",
			}),
		}),
		validateInvoicePosition: build.mutation({
			query: ({ projectId, invoiceId, positionId }) => ({
				url: `projects/${projectId}/aufmass/invoice-position/${invoiceId}/${positionId}/validate`,
				method: "POST",
			}),
		}),
		updatePositionComments: build.mutation({
			query: ({ projectId, invoiceId, positionId, comments }) => ({
				url: `projects/${projectId}/aufmass/invoice-position/${invoiceId}/${positionId}/comments`,
				method: "PATCH",
				body: { comments },
			}),
		}),
	}),
});

export const {
	useGetProjectAufmassQuery,
	useGetAufmassDetailsV2Query,
	useGetAufmassByIdQuery,
	useSaveAufmassDetailsMutation,
	useCreateAufmassMutation,
	useUpdateAufmassMutation,
	useDeleteAufmassMutation,
	useAddAufmassGroupMutation,
	useAddAufmassGroupV2Mutation,
	useUpdateAufmassGroupMutation,
	useDeleteAufmassGroupMutation,
	useAddAufmassLineMutation,
	useUpdateAufmassLineMutation,
	useDeleteAufmassLineMutation,
	useRevalidateAufmassMutation,
	useValidateInvoicePositionMutation,
	useUpdatePositionCommentsMutation,
} = aufmassApi;
